import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, CircleNotch, Warning } from "@phosphor-icons/react";
import { api } from "../lib/api";

export default function PaymentSuccess() {
  const location = useLocation();
  const sessionId = new URLSearchParams(location.search).get("session_id");
  const [state, setState] = useState("checking");
  const [portalToken, setPortalToken] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          if (data.portal_token) setPortalToken(data.portal_token);
          return;
        }
      } catch {
        // keep polling briefly — webhook may be in flight
      }
      attempts.current += 1;
      if (attempts.current < 15) {
        timer = setTimeout(poll, 2000);
      } else {
        setState("timeout");
      }
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-lg bg-card border border-border rounded-sm p-10 md:p-14 text-center"
      >
        {state === "checking" && (
          <>
            <CircleNotch size={44} weight="bold" className="text-accent mx-auto animate-spin" />
            <h1 className="font-serif text-3xl text-primary tracking-tight mt-6">Confirming your payment…</h1>
            <p className="mt-3 text-sm text-foreground/60">This takes a few seconds. Please don't close the tab.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle size={52} weight="fill" className="text-primary mx-auto" />
            <h1 className="font-serif text-3xl md:text-4xl text-primary tracking-tight mt-6" data-testid="payment-success-heading">
              You're in good hands now
            </h1>
            <p className="mt-4 text-foreground/70 leading-relaxed">
              Your payment is confirmed and a receipt is on its way to your inbox.
              We'll reach out within one business day to schedule your first consultation.
            </p>
            {portalToken && (
              <Link
                to={`/portal/${portalToken}`}
                data-testid="payment-portal-link"
                className="mt-8 inline-flex items-center gap-2 bg-accent text-accent-foreground px-8 py-4 rounded-full font-semibold text-sm hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
              >
                Open your secure consultation portal
              </Link>
            )}
            <p className="mt-3 text-xs text-foreground/45">Your private messaging thread with our team — no account needed, just this link.</p>
            <div>
              <Link
                to="/"
                data-testid="payment-success-home-link"
                className="mt-6 inline-block bg-primary text-primary-foreground px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-accent transition-colors duration-300"
              >
                Back to The Timeshare Edit
              </Link>
            </div>
          </>
        )}
        {(state === "failed" || state === "timeout") && (
          <>
            <Warning size={48} weight="duotone" className="text-accent mx-auto" />
            <h1 className="font-serif text-3xl text-primary tracking-tight mt-6" data-testid="payment-pending-heading">
              {state === "timeout" ? "We're still confirming" : "We couldn't find that payment"}
            </h1>
            <p className="mt-4 text-foreground/70 leading-relaxed">
              {state === "timeout"
                ? "Your payment went through but confirmation is taking longer than usual. If money left your account, you're all set — we'll confirm by email shortly."
                : "No payment session was found. If you believe you completed a payment, reply to your Stripe receipt and we'll sort it out personally."}
            </p>
            <Link
              to="/"
              data-testid="payment-pending-home-link"
              className="mt-8 inline-block bg-primary text-primary-foreground px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-accent transition-colors duration-300"
            >
              Back to the site
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
